import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  Clock,
  Check,
  Copy,
  Share2,
  Printer,
  Grid,
  List,
  ChevronRight,
  X,
  ExternalLink,
  Play,
  Pause,
  Bookmark,
  Layers,
  Sparkles,
  BookOpen,
  Workflow
} from 'lucide-react';
import { AdSlot } from './Monetization.jsx';
import { trackLeadEvent } from './utils/tracking.js';
import './blog-futuristic.css';

// --------------------------------------------------------------------------
// Helpers: Date & Read Time
// --------------------------------------------------------------------------
function formatDate(dateString) {
  if (!dateString) return 'Oct 2026';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch {
    return 'Oct 2026';
  }
}

function estimateReadingTime(post) {
  if (post.readingMinutes) return post.readingMinutes;
  const words = (post.excerpt || '').split(/\s+/).length * 8 + 800;
  return Math.max(3, Math.ceil(words / 220));
}

// --------------------------------------------------------------------------
// LocalStorage Bookmark Manager (100% Browser-Local)
// --------------------------------------------------------------------------
function getSavedNodes() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem('bizaipro_saved_blueprints');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function toggleSavedNode(slug) {
  if (typeof window === 'undefined') return false;
  try {
    const current = getSavedNodes();
    let updated;
    let isSaved = false;
    if (current.includes(slug)) {
      updated = current.filter((s) => s !== slug);
    } else {
      updated = [...current, slug];
      isSaved = true;
    }
    localStorage.setItem('bizaipro_saved_blueprints', JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('bizaipro-bookmark-change', { detail: { slug, isSaved, list: updated } }));
    return isSaved;
  } catch {
    return false;
  }
}

// --------------------------------------------------------------------------
// Apple-Style Smooth Interactive Custom Cursor
// --------------------------------------------------------------------------
export function AppleCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(pointer: coarse)').matches) return; // Touch devices

    let mouseX = -100;
    let mouseY = -100;
    let ringX = -100;
    let ringY = -100;
    let isHovering = false;
    let isVisible = false;
    let animId = null;

    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!isVisible) {
        isVisible = true;
        if (dotRef.current) dotRef.current.style.opacity = '1';
        if (ringRef.current) ringRef.current.style.opacity = '1';
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX - 3}px, ${mouseY - 3}px, 0)`;
      }

      // Check if hovering over clickable element
      const target = e.target;
      const clickable = target.closest('a, button, input, .apple-card, .apple-spotlight-card, [role="button"]');
      if (clickable && !isHovering) {
        isHovering = true;
        if (ringRef.current) ringRef.current.classList.add('active');
      } else if (!clickable && isHovering) {
        isHovering = false;
        if (ringRef.current) ringRef.current.classList.remove('active');
      }
    };

    const onMouseLeave = () => {
      isVisible = false;
      if (dotRef.current) dotRef.current.style.opacity = '0';
      if (ringRef.current) ringRef.current.style.opacity = '0';
    };

    const render = () => {
      // Smooth linear interpolation for the outer ring
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX - (isHovering ? 26 : 16)}px, ${ringY - (isHovering ? 26 : 16)}px, 0)`;
      }
      animId = requestAnimationFrame(render);
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);
    animId = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
      if (animId) cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="apple-cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="apple-cursor-ring" aria-hidden="true" />
    </>
  );
}

// --------------------------------------------------------------------------
// Apple-Style Geometric Abstract Card Art (When image is absent)
// --------------------------------------------------------------------------
export function AppleAbstractArt({ title }) {
  return (
    <div className="apple-abstract-art" aria-hidden="true">
      <div className="apple-abstract-shape">
        <Sparkles size={32} strokeWidth={1.5} />
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Apple Podcast-Style Minimalist Audio Briefing Player
// --------------------------------------------------------------------------
export function CyberAudioBriefingBar({ title, excerpt }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [rate, setRate] = useState(1.0);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      setSupported(true);
    }
  }, []);

  const handleTogglePlay = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;

    if (isPlaying) {
      synth.cancel();
      setIsPlaying(false);
      return;
    }

    synth.cancel();
    const speechText = `Article overview for ${title}. ${excerpt || 'Practical guide and implementation walkthrough from BizAIPro.'}`;
    const utterance = new SpeechSynthesisUtterance(speechText);
    utterance.rate = rate;

    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    synth.speak(utterance);
    setIsPlaying(true);
  };

  const handleRateChange = (newRate) => {
    setRate(newRate);
    if (isPlaying && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  return (
    <div className="apple-audio-card" role="region" aria-label="Audio summary player">
      <div className="apple-audio-left">
        <button
          type="button"
          onClick={handleTogglePlay}
          className="apple-play-circle"
          aria-label={isPlaying ? 'Pause audio summary' : 'Listen to article summary'}
          title={isPlaying ? 'Pause audio' : 'Listen to article'}
        >
          {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" style={{ marginLeft: '2px' }} />}
        </button>
        <div>
          <div className="apple-audio-title">{isPlaying ? 'Playing Audio Briefing...' : 'Listen to Article Summary'}</div>
          <div className="apple-audio-desc">Synthesized in-browser · 2-minute overview</div>
        </div>
      </div>

      <div className="apple-audio-speeds">
        {[1.0, 1.25, 1.5].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => handleRateChange(r)}
            className={`apple-speed-btn ${rate === r ? 'active' : ''}`}
          >
            {r}x
          </button>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Apple Clean Social Share Modal
// --------------------------------------------------------------------------
export function AppleShareModal({ isOpen, onClose, title, slug }) {
  if (!isOpen) return null;
  const currentUrl = typeof window !== 'undefined' ? `${window.location.origin}/${slug}/` : `https://bizaipro.com/${slug}/`;
  const shareText = encodeURIComponent(`${title} — BizAIPro Journal`);

  const copyToClipboard = () => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(currentUrl);
      onClose('Link copied to clipboard');
    }
  };

  return (
    <div className="apple-modal-overlay" onClick={() => onClose()} role="dialog" aria-modal="true" aria-label="Share article">
      <div className="apple-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="apple-modal-header">
          <span className="apple-modal-title">Share Article</span>
          <button type="button" onClick={() => onClose()} className="apple-clear-btn" aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <a
            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(currentUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="apple-workflow-item"
          >
            <span>Share on X (Twitter)</span>
            <ExternalLink size={14} />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="apple-workflow-item"
          >
            <span>Share on LinkedIn</span>
            <ExternalLink size={14} />
          </a>
          <a
            href={`https://api.whatsapp.com/send?text=${shareText}%20${encodeURIComponent(currentUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="apple-workflow-item"
          >
            <span>Share on WhatsApp</span>
            <ExternalLink size={14} />
          </a>
          <button
            type="button"
            onClick={copyToClipboard}
            className="apple-btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
          >
            <Copy size={15} />
            <span>Copy Link</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Apple Clean Card Component
// --------------------------------------------------------------------------
export function FuturisticCard({ post, index = 0, onBookmark }) {
  const [isSaved, setIsSaved] = useState(false);
  const readingTime = estimateReadingTime(post);
  const formattedDate = formatDate(post.publishedAt || post.updatedAt);

  useEffect(() => {
    const checkSaved = () => {
      setIsSaved(getSavedNodes().includes(post.slug));
    };
    checkSaved();
    window.addEventListener('bizaipro-bookmark-change', checkSaved);
    return () => window.removeEventListener('bizaipro-bookmark-change', checkSaved);
  }, [post.slug]);

  const handleBookmarkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const saved = toggleSavedNode(post.slug);
    setIsSaved(saved);
    if (onBookmark) onBookmark(post.slug, saved);
  };

  return (
    <article className="apple-card">
      <a className="apple-card-media" href={`/${post.slug}/`} tabIndex={-1} aria-hidden="true">
        {post.featuredImage ? (
          <img
            src={post.featuredImage}
            alt={post.imageAlt || post.title}
            loading="lazy"
            width="800"
            height="450"
            className="apple-card-img"
          />
        ) : (
          <AppleAbstractArt title={post.title} />
        )}
      </a>

      <button
        type="button"
        onClick={handleBookmarkClick}
        className={`apple-card-bookmark-btn ${isSaved ? 'saved' : ''}`}
        title={isSaved ? 'Remove from Saved' : 'Save article'}
        aria-label={isSaved ? 'Remove Bookmark' : 'Add Bookmark'}
      >
        <Bookmark size={15} fill={isSaved ? 'currentColor' : 'none'} />
      </button>

      <div className="apple-card-body">
        <div className="apple-card-meta-top">
          <span>{post.category || 'Guides'}</span>
        </div>

        <h3 className="apple-card-title">
          <a href={`/${post.slug}/`}>{post.title}</a>
        </h3>

        <p className="apple-card-excerpt">{post.excerpt}</p>

        <div className="apple-card-footer">
          <span>{formattedDate} · {readingTime} min read</span>
          <a className="apple-card-link" href={`/${post.slug}/`}>
            Read <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </article>
  );
}

// --------------------------------------------------------------------------
// Apple List Stream Row
// --------------------------------------------------------------------------
export function AppleStreamRow({ post }) {
  const readingTime = estimateReadingTime(post);
  const formattedDate = formatDate(post.publishedAt || post.updatedAt);

  return (
    <a href={`/${post.slug}/`} className="apple-stream-row">
      <div>
        <div style={{ fontSize: '11.5px', color: 'var(--apple-blue)', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>
          {post.category || 'Guides'}
        </div>
        <div className="apple-stream-title">{post.title}</div>
      </div>
      <div className="apple-stream-meta">
        <span>{readingTime} min</span>
        <span>{formattedDate}</span>
        <ArrowRight size={14} color="var(--apple-blue)" />
      </div>
    </a>
  );
}

// --------------------------------------------------------------------------
// Apple Minimalist Sidebar
// --------------------------------------------------------------------------
export function AppleTacticalSidebar({ settings }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setMsg('Please enter a valid work or personal email address.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setMsg('');
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          service: 'Weekly Growth Playbooks & 500+ Assets',
          source: 'blog-apple-sidebar',
          notes: 'Subscribed from clean blog sidebar'
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Subscription failed.');
      trackLeadEvent('Weekly Growth Playbooks', 'Newsletter', 'blog-apple-sidebar');
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMsg(err.message || 'Subscription failed. Please try again.');
    }
  };

  return (
    <aside className="apple-sidebar" aria-label="Editorial Sidebar">
      {/* Workflows Navigation */}
      <div className="apple-sidebar-card">
        <h3 className="apple-sidebar-heading">
          <Workflow size={16} color="var(--apple-blue)" />
          <span>Curated Workflows</span>
        </h3>
        <div className="apple-workflow-list">
          <a href="/ai-marketing-automation-small-business-playbook/" className="apple-workflow-item">
            <span>Lead Routing &amp; Attribution</span>
            <ChevronRight size={14} />
          </a>
          <a href="/tools/roas-calculator/" className="apple-workflow-item">
            <span>Marketing ROAS Calculator</span>
            <ChevronRight size={14} />
          </a>
          <a href="/ai-implementation-plan-small-business/" className="apple-workflow-item">
            <span>Small Business AI Roadmap</span>
            <ChevronRight size={14} />
          </a>
          <a href="/pdf-tools/" className="apple-workflow-item">
            <span>Free Private PDF Tools</span>
            <ChevronRight size={14} />
          </a>
          <a href="/downloads/" className="apple-workflow-item">
            <span>500+ Free Digital Assets</span>
            <ChevronRight size={14} />
          </a>
        </div>
      </div>

      {/* Clean Newsletter Subscription */}
      <div className="apple-newsletter-card">
        <h4 className="apple-newsletter-title">Weekly AI Briefing</h4>
        <p className="apple-newsletter-desc">
          Get actionable playbooks and free tools delivered to your inbox every Thursday. Zero spam.
        </p>

        {status === 'success' ? (
          <div className="apple-newsletter-success">
            <Check size={16} />
            <span>You're all set! Welcome aboard.</span>
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="apple-newsletter-form">
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'loading'}
              className="apple-newsletter-input"
              aria-label="Email for newsletter"
            />
            <button type="submit" disabled={status === 'loading'} className="apple-newsletter-btn">
              {status === 'loading' ? 'Joining...' : 'Subscribe'}
            </button>
            {msg && <span style={{ color: '#ff3b30', fontSize: '11px' }}>{msg}</span>}
          </form>
        )}
      </div>

      {/* AdSense Ad Container */}
      {settings?.adsenseSidebarSlot && (
        <div className="apple-sidebar-card" style={{ padding: '16px' }}>
          <AdSlot settings={settings} slot={settings.adsenseSidebarSlot} type="sidebar" />
        </div>
      )}
    </aside>
  );
}

// --------------------------------------------------------------------------
// Apple Clean Author Dossier (Bottom of Article)
// --------------------------------------------------------------------------
export function CyberAuthorDossier({ author, date }) {
  return (
    <div className="apple-author-card" role="region" aria-label="Author information">
      <div className="apple-author-card-avatar">B</div>
      <div className="apple-author-card-body">
        <h3 className="apple-author-card-title">{author || 'BizAIPro Editorial'}</h3>
        <p className="apple-author-card-bio">
          Published by the BizAIPro editorial and engineering team. Practical AI frameworks, automation workflows, and free calculators built specifically for small business owners and operators.
        </p>
        <span style={{ fontSize: '12.5px', color: 'var(--apple-text-tertiary)' }}>
          Published: {formatDate(date)}
        </span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Primary Apple-Clean Blog Hub Component
// --------------------------------------------------------------------------
export default function FuturisticBlogHub({ data }) {
  const searchInputRef = useRef(null);
  const [query, setQuery] = useState(data.query || '');
  const [durationFilter, setDurationFilter] = useState('all'); // 'all' | 'quick' | 'deep' | 'master' | 'saved'
  const [viewMode, setViewMode] = useState('grid');
  const [toastMsg, setToastMsg] = useState('');
  const [savedCount, setSavedCount] = useState(0);

  const posts = data.posts || [];
  const currentPage = data.page || 1;
  const totalPosts = data.total || posts.length;

  // Sync saved count
  useEffect(() => {
    const updateCount = () => {
      setSavedCount(getSavedNodes().length);
    };
    updateCount();
    window.addEventListener('bizaipro-bookmark-change', updateCount);
    return () => window.removeEventListener('bizaipro-bookmark-change', updateCount);
  }, []);

  // Keyboard shortcut Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setQuery('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter posts
  const filteredPosts = useMemo(() => {
    let result = posts;
    if (query.trim()) {
      const q = query.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(q) ||
          p.excerpt?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q)
      );
    }
    if (durationFilter === 'quick') {
      result = result.filter((p) => estimateReadingTime(p) <= 5);
    } else if (durationFilter === 'deep') {
      result = result.filter((p) => estimateReadingTime(p) > 5 && estimateReadingTime(p) <= 10);
    } else if (durationFilter === 'master') {
      result = result.filter((p) => estimateReadingTime(p) > 10);
    } else if (durationFilter === 'saved') {
      const savedList = getSavedNodes();
      result = result.filter((p) => savedList.includes(p.slug));
    }
    return result;
  }, [posts, query, durationFilter]);

  // Spotlight post on page 1
  const spotlightPost = currentPage === 1 && !query && durationFilter === 'all' && filteredPosts.length > 0
    ? filteredPosts[0]
    : null;

  const gridPosts = spotlightPost ? filteredPosts.slice(1) : filteredPosts;
  const currentCategorySlug = data.path?.match(/^\/category\/([^/]+)/)?.[1] || null;

  const categories = [
    { label: 'All Guides', url: '/blog/', slug: null },
    { label: 'AI & Automation', url: '/category/ai-automation/', slug: 'ai-automation' },
    { label: 'Marketing', url: '/category/marketing/', slug: 'marketing' },
    { label: 'Operations', url: '/category/operations/', slug: 'operations' },
    { label: 'Strategy', url: '/category/strategy/', slug: 'strategy' },
    { label: 'Guides', url: '/category/guides/', slug: 'guides' }
  ];

  const handleBookmarkNotification = (slug, isSaved) => {
    setToastMsg(isSaved ? 'Article saved to your list' : 'Article removed from saved');
    setTimeout(() => setToastMsg(''), 2500);
  };

  return (
    <div className="apple-blog-page">
      {/* Apple Smooth Interactive Cursor */}
      <AppleCursor />

      {/* Subtle Top Navigation Telemetry Bar */}
      <div className="apple-blog-subnav">
        <div className="container apple-blog-subnav-inner">
          <div className="apple-blog-subnav-left">
            <span className="apple-live-badge">
              <span className="apple-live-dot" />
              <span>BizAIPro Journal</span>
            </span>
            <span>·</span>
            <span style={{ color: 'var(--apple-text-tertiary)' }}>Updated October 2026</span>
          </div>
          <div className="apple-blog-subnav-right">
            <a href="/free-tools/" className="apple-subnav-link">
              <span>Free Tools</span>
              <ArrowUpRight size={13} />
            </a>
            <a href="/downloads/" className="apple-subnav-link">
              <span>Free Assets</span>
              <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </div>

      {/* Hero Header */}
      <section className="apple-hero-section">
        <div className="container apple-hero-container">
          <span className="apple-eyebrow">Practical Knowledge</span>
          <h1 className="apple-hero-title">Ideas into action.<br />Real business progress.</h1>
          <p className="apple-hero-desc">
            Tested AI implementation frameworks, marketing automation playbooks, and decision calculators designed for small businesses.
          </p>

          {/* Spotlight Search Bar */}
          <form className="apple-search-box" action="/search/" onSubmit={(e) => {}}>
            <Search size={18} className="apple-search-icon" />
            <input
              ref={searchInputRef}
              name="q"
              type="text"
              placeholder="Search guides, workflows, or calculators..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="apple-search-input"
              aria-label="Search articles and tools"
            />
            <div className="apple-search-right">
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="apple-clear-btn"
                  aria-label="Clear search"
                >
                  <X size={15} />
                </button>
              )}
              <span className="apple-kbd">⌘K</span>
            </div>
          </form>
        </div>
      </section>

      {/* Category Tabs & Secondary Controls */}
      <div className="container apple-filter-section">
        <div className="apple-filter-container">
          {/* Scrollable Category Segmented Pills */}
          <nav className="apple-categories-scroll" aria-label="Categories">
            {categories.map((c) => {
              const isActive = c.slug === currentCategorySlug || (!c.slug && !currentCategorySlug);
              return (
                <a
                  key={c.url}
                  href={c.url}
                  className={`apple-cat-pill ${isActive ? 'active' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {c.label}
                </a>
              );
            })}
          </nav>

          {/* Secondary Controls: Duration Filter & View Switch */}
          <div className="apple-secondary-filters">
            <div className="apple-duration-pills">
              <span className="apple-filter-label">Filter:</span>
              {[
                { id: 'all', label: 'All' },
                { id: 'quick', label: 'Under 5 min' },
                { id: 'deep', label: '5–10 min' },
                { id: 'master', label: '10+ min' }
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setDurationFilter(f.id)}
                  className={`apple-mini-pill ${durationFilter === f.id ? 'active' : ''}`}
                >
                  {f.label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setDurationFilter(durationFilter === 'saved' ? 'all' : 'saved')}
                className={`apple-mini-pill ${durationFilter === 'saved' ? 'active' : ''}`}
                style={{ marginLeft: '6px' }}
              >
                ★ Saved ({savedCount})
              </button>
            </div>

            <div className="apple-view-switch" role="group" aria-label="Layout view">
              <button
                type="button"
                className={`apple-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <Grid size={14} />
                <span>Grid</span>
              </button>
              <button
                type="button"
                className={`apple-view-btn ${viewMode === 'stream' ? 'active' : ''}`}
                onClick={() => setViewMode('stream')}
                title="List View"
              >
                <List size={14} />
                <span>List</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="container">
        {/* Featured Editorial Spotlight (Page 1 Only) */}
        {spotlightPost && (
          <section className="apple-spotlight-card" aria-label="Featured guide">
            <div className={`apple-spotlight-grid${spotlightPost.featuredImage ? "" : " apple-spotlight-text-only"}`}>
              {spotlightPost.featuredImage && <div className="apple-spotlight-media">
                {spotlightPost.featuredImage ? (
                  <img
                    src={spotlightPost.featuredImage}
                    alt={spotlightPost.imageAlt || spotlightPost.title}
                    className="apple-spotlight-img"
                    width="960"
                    height="540"
                  />
                ) : (
                  <AppleAbstractArt title={spotlightPost.title} />
                )}
              </div>}

              <div className="apple-spotlight-body">
                <div>
                  <span className="apple-spotlight-tag">{spotlightPost.category || 'Featured Guide'}</span>
                  <h2 className="apple-spotlight-title">
                    <a href={`/${spotlightPost.slug}/`}>{spotlightPost.title}</a>
                  </h2>
                  <p className="apple-spotlight-excerpt">{spotlightPost.excerpt}</p>
                </div>

                <div className="apple-spotlight-foot">
                  <div className="apple-author-meta">
                    <div className="apple-author-avatar">B</div>
                    <div>
                      <div className="apple-author-name">{spotlightPost.author || 'BizAIPro Editorial'}</div>
                      <div className="apple-meta-sub">{formatDate(spotlightPost.publishedAt)} · {estimateReadingTime(spotlightPost)} min read</div>
                    </div>
                  </div>
                  <a className="apple-btn-primary" href={`/${spotlightPost.slug}/`}>
                    <span>Read guide</span>
                    <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Two-Column Grid & Sidebar Layout */}
        <div className="apple-main-layout">
          <main id="apple-articles-stream">
            {filteredPosts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '64px 20px', background: 'var(--apple-bg-secondary)', borderRadius: '20px' }}>
                <BookOpen size={36} color="var(--apple-blue)" style={{ margin: '0 auto 16px' }} />
                <h3 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 8px' }}>No guides found</h3>
                <p style={{ color: 'var(--apple-text-secondary)', maxWidth: '400px', margin: '0 auto 20px', fontSize: '14px' }}>
                  {durationFilter === 'saved'
                    ? "You haven't saved any articles yet. Click the bookmark icon on any card to save articles for later."
                    : `No results match your current filter. Try searching for other topics or reset filters.`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setDurationFilter('all');
                  }}
                  className="apple-btn-primary"
                  style={{ margin: '0 auto' }}
                >
                  View all guides
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="apple-cards-grid">
                {gridPosts.map((post, i) => (
                  <FuturisticCard
                    key={post._id || post.slug}
                    post={post}
                    index={spotlightPost ? i + 1 : i}
                    onBookmark={handleBookmarkNotification}
                  />
                ))}
              </div>
            ) : (
              <div className="apple-stream-list">
                {filteredPosts.map((post) => (
                  <AppleStreamRow key={post._id || post.slug} post={post} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {(currentPage > 1 || totalPosts > currentPage * 12) && (
              <nav className="apple-pagination" aria-label="Pagination">
                {currentPage > 1 && (
                  <a className="apple-page-btn" href={`?page=${currentPage - 1}`}>
                    Previous
                  </a>
                )}
                <span className="apple-page-indicator">
                  Page {currentPage} of {Math.ceil(totalPosts / 12)}
                </span>
                {totalPosts > currentPage * 12 && (
                  <a className="apple-page-btn" href={`?page=${currentPage + 1}`}>
                    Next
                  </a>
                )}
              </nav>
            )}
          </main>

          {/* Minimalist Sidebar */}
          <AppleTacticalSidebar settings={data.settings} />
        </div>
      </div>

      {/* Floating Toast Notification */}
      {toastMsg && (
        <div className="apple-toast" role="status">
          <Check size={16} />
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Article Reading HUD (Apple News Style)
// --------------------------------------------------------------------------
export function FuturisticArticleReaderHUD({ title, slug, excerpt }) {
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(getSavedNodes().includes(slug));

    let frame = 0;
    const handleScroll = () => {
      frame = 0;
      const el = document.documentElement;
      const winScroll = el.scrollTop || document.body.scrollTop;
      const height = el.scrollHeight - el.clientHeight;
      const scrolled = height > 0 ? Math.min(100, Math.max(0, Math.round((winScroll / height) * 100))) : 0;
      setProgress(scrolled);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      cancelAnimationFrame(frame);
    };
  }, [slug]);

  const handleBookmark = () => {
    const saved = toggleSavedNode(slug);
    setIsSaved(saved);
    setToast(saved ? 'Saved to reading list' : 'Removed from reading list');
    setTimeout(() => setToast(''), 2500);
  };

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  const handleShareClose = (msg) => {
    setShareOpen(false);
    if (msg) {
      setToast(msg);
      setTimeout(() => setToast(''), 2500);
    }
  };

  return (
    <>
      <AppleCursor />
      {/* Top 3px Reading Progress Line */}
      <div className="apple-reading-progress">
        <div className="apple-reading-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {/* Interactive HUD Toolbar */}
      <div className="apple-reading-hud" role="region" aria-label="Reading controls">
        <div className="container apple-reading-hud-inner">
          <div style={{ fontSize: '13px', color: 'var(--apple-text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: '600', color: 'var(--apple-blue)' }}>{progress}% read</span>
          </div>

          <div className="apple-hud-actions">
            <button
              type="button"
              onClick={handleBookmark}
              className={`apple-hud-btn ${isSaved ? 'active' : ''}`}
              title={isSaved ? 'Remove from Saved' : 'Save article'}
            >
              <Bookmark size={13} fill={isSaved ? 'currentColor' : 'none'} />
              <span>{isSaved ? 'Saved' : 'Save'}</span>
            </button>

            <button type="button" onClick={() => setShareOpen(true)} className="apple-hud-btn" title="Share article">
              <Share2 size={13} />
              <span>Share</span>
            </button>

            <button type="button" onClick={handlePrint} className="apple-hud-btn" title="Print article">
              <Printer size={13} />
              <span>Print</span>
            </button>

            <a href="/blog/" className="apple-hud-btn" title="Back to Journal">
              <span>All Guides</span>
              <ChevronRight size={13} />
            </a>
          </div>
        </div>
      </div>

      <AppleShareModal isOpen={shareOpen} onClose={handleShareClose} title={title} slug={slug} />

      {toast && (
        <div className="apple-toast" role="status">
          <Check size={16} />
          <span>{toast}</span>
        </div>
      )}
    </>
  );
}
